import { useToasterStore } from "@/modules/auth/store/toaster";
import Toast from "../Toast";
import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";
import { Button } from "../Button";

const DISMISS_DELAY = 5000;

const Toaster = () => {
  const {
    toasts,
    removeToast,
    removeToasts,
    startPolling,
    stopPolling,
  } = useToasterStore();
  const [expanded, setExpanded] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { t } = useTranslation();
  const [hideForMinute, setHideForMinute] = useState(false);

  const showToasts = () => {
    if (expanded) return;
    setExpanded(!expanded);
  };

  const hideToasts = () => {
    stopPolling();
    removeToasts();
    setHideForMinute(true);
    // setTimeout(() => {
    //   setHideForMinute(false);
    //   startPolling(t, i18n.language);
    // }, 60000);
    setExpanded(false);
  };

  useEffect(() => {
    if (hideForMinute) return;
    startPolling(t, i18n.language);
  }, [t, hideForMinute, startPolling]);

  const visibleToasts = expanded
    ? toasts.slice().reverse()
    : toasts.slice(0, 3);

  const lastToast = expanded
    ? visibleToasts[visibleToasts.length - 1]
    : visibleToasts[0];

  useEffect(() => {
    if (!lastToast) return;

    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    timeoutRef.current = setTimeout(() => {
      removeToast(lastToast.id);
    }, DISMISS_DELAY);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [lastToast, expanded, removeToast]);

  if (visibleToasts.length === 0 || hideForMinute) return null;

  return (
    <div
      className={`z-[9999] fixed left-0 top-0 w-full flex flex-col gap-2 px-5 ${
        !expanded && "max-h-[6.25rem]"
      }`}
    >
      <div
        className={`w-full pt-4 overflow-scroll ${
          expanded && "max-h-[20rem] flex flex-col-reverse"
        }`}
      >
        <button onClick={showToasts} className="w-full">
          <AnimatePresence mode="popLayout">
            <div key={expanded ? "expanded" : "collapsed"}>
              {visibleToasts.map(({ id, title, timeAgo, message }, index) => {
                const offset = expanded ? 0 : index * (50 + index * 3);
                const zIndex = expanded ? 1 : visibleToasts.length - index;
                const baseScale = 1;
                const scaleStep = 0.04;
                const scale = expanded ? 1 : baseScale - scaleStep * index;
                const exitAnimation = expanded
                  ? {
                      opacity: 0,
                      y: -20,
                      scale: 0.9,
                      transition: { duration: 0.3 },
                    }
                  : {};
                const animate = expanded
                  ? {}
                  : {
                      opacity: 1,
                      y: 0,
                      scale,
                      transition: { duration: 0.5 },
                    };
                const initialState = expanded
                  ? {}
                  : { opacity: 1, y: -50, scale: 0.95 };

                return (
                  <motion.div
                    drag="x"
                    dragConstraints={{ left: 0, right: 0 }}
                    onDragEnd={(_, info) => {
                      if (Math.abs(info.offset.x) > 80) {
                        removeToast(id);
                      }
                    }}
                    initial={initialState}
                    animate={animate}
                    exit={exitAnimation}
                    layout
                    key={id}
                    className="relative"
                    style={{ zIndex: zIndex }}
                  >
                    <div
                      style={{
                        transform: `translateY(-${offset}px) scale(${scale})`,
                      }}
                    >
                      <Toast
                        title={title}
                        timeAgo={timeAgo}
                        message={message}
                      />
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </AnimatePresence>
        </button>
      </div>

      {expanded && (
        <div className="w-full flex items-center justify-end">
          <Button
            onClick={hideToasts}
            text={t("events.clear")}
            size="small"
            mode="event"
            className={`!font-normal !py-1.5`}
            pilled
          />
        </div>
      )}
    </div>
  );
};

export default Toaster;
